import cx from "classnames";
import { ChangeEvent, DragEvent, forwardRef, useId, useState } from "react";

import { CloudUpload, DropFile, File, Trash } from "components/icons/generated";
import ButtonIcon from "ds/components/ButtonIcon";
import Icon from "ds/components/Icon";
import LoadingIndicator from "ds/components/LoadingIndicator";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import { withTestId } from "utils/withTestId";

import Box from "../../Box";
import { FILE_INPUT_TEST_ID } from "./constants";
import styles from "./styles.module.css";

type DragDropFileUploadProps = {
  ariaLabel?: string;
  name?: string;
  caption?: string;
  className?: string;
  textAlign?: "left" | "center";
  dragBoxClassName?: string;
  fileListClassName?: string;
  file: File | null;
  loading?: boolean;
  onChange: (file: File | null) => void;
};

const DragDropFileUpload = forwardRef(function DragDropFileUpload(
  {
    onChange,
    ariaLabel,
    file,
    name,
    caption = "Drag and drop the file here",
    className,
    textAlign = "center",
    dragBoxClassName,
    fileListClassName,
    loading,
  }: DragDropFileUploadProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const id = useId();
  const [isDragActivated, setIsDragActivated] = useState(false);

  const disableDragActivatedMode = () => {
    setIsDragActivated(false);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActivated(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const file = e.dataTransfer.files[0];
    if (file) {
      onChange(file);
    }

    disableDragActivatedMode();
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    // Prevent default behavior (Prevent file from being opened)
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const file = e.target.files?.length && e.target.files[0];
    if (file) {
      onChange(file);
    }
  };

  return file ? (
    <Box
      direction="row"
      justify="between"
      align="center"
      gap="large"
      className={cx(styles.uploadedFileField, className, fileListClassName)}
    >
      <Box direction="row" align="center" gap="medium">
        <Icon src={File} className={styles.docIcon} />
        <TextEllipsis tooltip={file.name} tooltipWidthMode="maxWidthXl">
          {(props) => (
            <Typography {...props} tag="span">
              {file.name}
            </Typography>
          )}
        </TextEllipsis>
      </Box>
      <LoadingIndicator size="large" loading={loading}>
        <ButtonIcon icon={Trash} variant="ghostDanger" onPress={() => onChange(null)}>
          Delete
        </ButtonIcon>
      </LoadingIndicator>
    </Box>
  ) : (
    <Box
      direction="column"
      align="center"
      justify="center"
      fullWidth
      className={cx(
        styles.dropArea,
        styles[textAlign],
        { [styles.dragActivated]: isDragActivated },
        className,
        dragBoxClassName
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={disableDragActivatedMode}
      onDragEnd={disableDragActivatedMode}
    >
      <input
        aria-label={ariaLabel || "Drop the file here or browse files"}
        name={name}
        type="file"
        id={id}
        ref={ref}
        onChange={handleInputChange}
        {...withTestId(FILE_INPUT_TEST_ID)}
      />
      <Box
        direction={textAlign === "left" ? "row" : "column"}
        align={textAlign === "left" ? "start" : "center"}
        gap={textAlign === "left" ? "medium" : "0"}
      >
        <Icon src={CloudUpload} className={styles.fileUploadIcon} />
        <Icon src={DropFile} className={styles.onDropIcon} />
        <Box
          className={styles.fileUploadCaption}
          direction="column"
          align={textAlign === "left" ? "start" : "center"}
        >
          <Typography tag="p">{caption}</Typography>
          <Typography aria-hidden htmlFor={id} tag="label" className={styles.fileUploadLink}>
            or browse files
          </Typography>
        </Box>
        <Typography tag="span" className={styles.onDropCaption}>
          Drop the file here
        </Typography>
      </Box>
    </Box>
  );
});

DragDropFileUpload.displayName = "DS.FileUpload.DragDropFileUpload";

export default DragDropFileUpload;
